import type { NotificationCategory } from '@/services/notificationService';

// Legacy interface used by the header NotificationsPanel dropdown (mock data)
export interface Notification {
  id: string;
  type: 'order' | 'payment' | 'content' | 'system' | 'deal' | 'support';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  actionLabel?: string;
  metadata?: {
    orderId?: string;
    amount?: number;
    status?: string;
  };
}

export const mockNotifications: Notification[] = [
  {
    id: 'notif-1',
    type: 'order',
    title: 'Order Completed',
    message: 'Your guest post order #ORD-2847 has been successfully delivered.',
    timestamp: new Date(Date.now() - 1000 * 60 * 15),
    read: false,
    actionUrl: '/orders',
    actionLabel: 'View Order',
    metadata: { orderId: 'ORD-2847', status: 'completed' },
  },
  {
    id: 'notif-2',
    type: 'payment',
    title: 'Payment Received',
    message: 'Your wallet has been credited with $500.00.',
    timestamp: new Date(Date.now() - 1000 * 60 * 45),
    read: false,
    actionUrl: '/wallet',
    actionLabel: 'View Wallet',
    metadata: { amount: 500 },
  },
  {
    id: 'notif-3',
    type: 'content',
    title: 'Content Ready for Review',
    message: 'Article "Top 10 SEO Strategies" is ready for your review.',
    timestamp: new Date(Date.now() - 1000 * 60 * 90),
    read: false,
    actionUrl: '/content-orders',
    actionLabel: 'Review Content',
    metadata: { orderId: 'CNT-1523' },
  },
  {
    id: 'notif-5',
    type: 'order',
    title: 'Order Update',
    message: 'Your visual link order #ORD-2846 is now in progress.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5),
    read: true,
    actionUrl: '/orders',
    actionLabel: 'Track Order',
    metadata: { orderId: 'ORD-2846', status: 'in-progress' },
  },
  {
    id: 'notif-6',
    type: 'support',
    title: 'Support Ticket Resolved',
    message: 'Your support ticket #TKT-893 has been resolved.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8),
    read: true,
    actionUrl: '/help-center',
    actionLabel: 'View Ticket',
  },
  {
    id: 'notif-7',
    type: 'system',
    title: 'Platform Maintenance',
    message:
      'Scheduled maintenance on Aug 10, 2025 from 2:00 AM - 4:00 AM UTC.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
    read: true,
    actionUrl: undefined,
    actionLabel: undefined,
  },
  {
    id: 'notif-8',
    type: 'payment',
    title: 'Invoice Generated',
    message: 'Your monthly invoice for July 2025 is now available.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2),
    read: true,
    actionUrl: '/wallet',
    actionLabel: 'View Invoice',
    metadata: { amount: 1250 },
  },
];

export const getNotificationIcon = (type: Notification['type'] | NotificationCategory | string) => {
  switch (type) {
    case 'order':
      return 'Package';
    case 'payment':
      return 'DollarSign';
    case 'content':
      return 'FileText';
    case 'task':
      return 'FileText';
    case 'system':
      return 'AlertCircle';
    case 'deal':
      return 'Tag';
    case 'support':
    case 'ticket':
      return 'MessageSquare';
    case 'site':
      return 'Globe';
    case 'dispute':
      return 'Shield';
    default:
      return 'Bell';
  }
};

export const getNotificationColor = (type: Notification['type'] | NotificationCategory | string) => {
  switch (type) {
    case 'order':
      return { bg: '#E3F2FD', text: '#568EF5', border: '#568EF5' };
    case 'payment':
      return { bg: '#E6F4EC', text: '#27AE60', border: '#27AE60' };
    case 'content':
      return { bg: '#EDE7F6', text: '#747ED1', border: '#747ED1' };
    case 'task':
      return { bg: '#EDE7F6', text: '#747ED1', border: '#747ED1' };
    case 'system':
      return { bg: '#FFF9E6', text: '#FFC107', border: '#FFC107' };
    case 'deal':
      return { bg: '#FFE5E5', text: '#FF6B35', border: '#FF6B35' };
    case 'support':
    case 'ticket':
      return { bg: '#E3F2FD', text: '#3B82F6', border: '#3B82F6' };
    case 'site':
      return { bg: '#EDE7F6', text: '#8B5CF6', border: '#8B5CF6' };
    case 'dispute':
      return { bg: '#FEE2E2', text: '#DC2626', border: '#DC2626' };
    default:
      return { bg: '#F3F4F6', text: '#4C5157', border: '#E1E6EC' };
  }
};

// Overloaded: accepts Date (legacy) or string (API)
export const formatNotificationTime = (timestamp: Date | string): string => {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};
